import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { Globe, CheckCircle, Eye, EyeOff } from 'lucide-react'
import { useUserStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import LanguageSelector from '../../components/LanguageSelector'
import PolicyModal from '../../components/PolicyModal'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import { authAPI } from '../../services/api'
import OrellaPageHeader from '../../components/OrellaPageHeader'
import Turnstile from '../../components/Turnstile'
import SmsConsentText from '../../components/SmsConsentText'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'
import handIcon from '../../assets/hand_icon.webp'
// s95: Google/Apple sign-up moved to the Welcome screen (hooks/useSsoSignIn.js). This
// screen is the email sign-up only.

/* ── Refreshed field style ── */
const fieldStyle = {
  height: 48, borderRadius: 12, border: '1.5px solid #CBD5E1',
  padding: '0 14px', color: '#15354F', backgroundColor: '#F8FAFC', fontSize: 14,
}
const fieldFocus = (e) => { e.target.style.borderColor = '#46A8F9'; e.target.style.backgroundColor = '#fff' }
const fieldBlur = (e) => { e.target.style.borderColor = '#CBD5E1'; e.target.style.backgroundColor = '#F8FAFC' }
const Label = ({ children }) => (
  <label className="block text-[13px] font-semibold mb-1.5" style={{ color: '#15354F' }}>
    {children}
  </label>
)
const Req = () => <span className="text-red-500 ml-0.5">*</span>


export default function SignUpScreen() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { register, loading, error, clearError } = useUserStore()
  const setUser = useUserStore((s) => s.setUser)
  const updateProfile = useProfileStore((state) => state.updateProfile)
  const { t, currentLanguage } = useI18n()
  const [showLanguageModal, setShowLanguageModal] = useState(false)
  const [agreeTerms, setAgreeTerms] = useState(false)
  const [smsOptIn, setSmsOptIn] = useState(false)
  const [showPolicy, setShowPolicy] = useState(null)
  
  // ★ v242.157.0 — USERSTORE-TRANSIENT-PERSISTED-1, the second omission. This
  //   screen renders `localError || error` (see below) exactly as SignInScreen
  //   does, so it inherited the same stale-message behaviour and would have
  //   kept it if the fix had stopped at the screen that was screenshotted.
  useEffect(() => {
    clearError()
  }, [])

  const inviteCode = searchParams.get('inviteCode')
  const isInviteFlow = !!inviteCode
  
  // v213.2 (C-A-159): Restore previously-typed signup fields from sessionStorage
  // so the form survives back-navigation, refresh, and tab restore. Password is
  // never persisted — patient re-enters it.
  // ★★★ PROD-614 (v242.238.2): no patient field is ever read from the URL, in any flow. The
  //   invite flow used to arrive as /signup?firstName=…&lastName=…&email=…&phone=…&inviteCode=…
  //   (PHI in browser history, the Referer and edge logs), and this form and its draft took those
  //   values. Now /join sends only the invite code and loadInvitePrefill below asks verifyInvite.
  //   No non-invite flow puts a patient field on /signup (measured 2026-10-06: the front door
  //   passes the e-mail in router state; the API's only patient-app link is /join?invite=&case=),
  //   so the URL reads were removed outright, not kept for another flow. The draft therefore
  //   cannot take a patient field from the URL either. Held by scripts/check-invite-prefill.mjs (IP-8).
  const SIGNUP_DRAFT_KEY = 'signup_form_draft'
  // s95d: from the front door (/email) the email arrives in router state, already checked
  //   as not registered; it is shown locked with "Change".
  const location = useLocation()
  const frontEmail = location.state?.fromFrontDoor ? (location.state.email || '') : ''
  // ★★ PROD-619 (patient P2): THE INVITE FLOW HAS NO DRAFT. Its name, e-mail and phone are the
  //   invite record's (loadInvitePrefill asks verifyInvite) and are shown read-only. Seeded from
  //   the draft, a shared tab's stale draft filled any field the next invite left empty - read-only,
  //   so the patient could not even correct it - and an abandoned invite sign-up kept the
  //   verifyInvite name, e-mail and phone in sessionStorage until the tab closed. Now the invite
  //   flow neither reads nor writes the draft, and removes one it finds (an older build's invite
  //   draft must not reach the next open sign-up in this tab). The open sign-up keeps its draft.
  //   Held by scripts/check-invite-prefill.mjs (IP-8, IP-11).
  const initialForm = (() => {
    const base = {
      firstName: '', // PROD-614
      lastName: '',
      phone: '',
      email: frontEmail || '',
      password: ''
    }
    if (isInviteFlow) return base // PROD-619
    try {
      const raw = sessionStorage.getItem(SIGNUP_DRAFT_KEY)
      if (raw) {
        const draft = JSON.parse(raw) || {}
        return {
          firstName: base.firstName || draft.firstName || '',
          lastName: base.lastName || draft.lastName || '',
          phone: base.phone || draft.phone || '',
          email: frontEmail || base.email || draft.email || '',
          password: ''
        }
      }
    } catch {}
    return base
  })()
  
  const [form, setForm] = useState(initialForm)
  const [localError, setLocalError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showConfirm, setShowConfirm] = useState(false)
  const [contentReady, setContentReady] = useState(!!sessionStorage.getItem('signup_animated'))

  // v239.13.0 (C-A-75): Turnstile token for open (non-invite) signup. turnstileKey
  // remounts the widget to issue a fresh, single-use token after a failed submit.
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [turnstileKey, setTurnstileKey] = useState(0)

  // Basic password requirements + confirm-match (v228.8.2)
  const pwReqs = {
    len: (form.password || '').length >= 8,
    letter: /[A-Za-z]/.test(form.password || ''),
    number: /\d/.test(form.password || ''),
  }
  const pwValid = pwReqs.len && pwReqs.letter && pwReqs.number
  const pwMatch = confirmPassword.length > 0 && confirmPassword === form.password
  const canSubmit = pwValid && pwMatch && (isInviteFlow || (agreeTerms && !!turnstileToken))
  
  // v213.2 (C-A-159): Persist form (without password) every time it changes.
  // Cleared on successful submission below.
  // PROD-619: not in the invite flow, which removes any draft instead (see initialForm).
  useEffect(() => {
    try {
      if (isInviteFlow) { sessionStorage.removeItem(SIGNUP_DRAFT_KEY); return }
      const { password, ...rest } = form
      sessionStorage.setItem(SIGNUP_DRAFT_KEY, JSON.stringify(rest))
    } catch {}
  }, [form, isInviteFlow])
  
  // ★★ PROD-611 (v242.238.1): in the invite flow the patient's name, e-mail and phone come from
  //   verifyInvite (GET /auth/verify-invite/<code>), the record the doctor created and the one
  //   register-invite activates. Its values win over a sessionStorage draft, so the required name
  //   fields are filled although the link that led here carries none (PROD-614, v242.238.2: the
  //   URL carries only the invite code; the effect that copied URL fields into the form is gone). A failed or invalid verification leaves the
  //   form as it is; register-invite answers that at submit. Held by scripts/check-invite-prefill.mjs.
  const loadInvitePrefill = async () => {
    if (!inviteCode) return
    let invite = null
    try { invite = await authAPI.verifyInvite(inviteCode) } catch { return }
    if (!invite || !invite.valid) return
    setForm(prev => ({
      ...prev,
      firstName: invite.firstName || prev.firstName,
      lastName: invite.lastName || prev.lastName,
      phone: invite.phone || prev.phone,
      email: invite.email || prev.email,
    }))
  }
  useEffect(() => {
    if (isInviteFlow) loadInvitePrefill()
  }, [inviteCode, isInviteFlow])

  /* ── Registration logic (unchanged) ── */
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError(null); setIsSubmitting(true)
    if (!form.password) { setLocalError(t('errors.fieldRequired')); setIsSubmitting(false); return }
    if (form.password.length < 8) { setLocalError(t('errors2.passwordMin8')); setIsSubmitting(false); return }
    if (!pwValid) { setLocalError(t('auth.passwordRequirements', 'Password must be at least 8 characters and include a letter and a number.')); setIsSubmitting(false); return }
    if (confirmPassword !== form.password) { setLocalError(t('auth.passwordsNoMatch', "Passwords don't match.")); setIsSubmitting(false); return }
    
    if (isInviteFlow && inviteCode) {
      try {
        const result = await authAPI.registerWithInvite(inviteCode, form.password, null)
        if (result.success) {
          // PROD-616: the profile basics only, never the form (it carries the password).
          updateProfile({ firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone })
          // v213.2 (C-A-159): clear signup draft on success
          try { sessionStorage.removeItem(SIGNUP_DRAFT_KEY) } catch {}
          if (result.needsPin) { navigate('/security', { replace: true, state: { userId: result.userId, fromInvite: true } }) }
          else if (result.token) { navigate('/home', { replace: true }) }
        } else { setLocalError(result.error || t('errors.somethingWrong')) }
      } catch (err) { setLocalError(err.message || err.error || t('errors.somethingWrong')) }
      finally { setIsSubmitting(false) }
      return
    }
    
    if (!form.email || !form.firstName || !form.lastName) { setLocalError(t('errors.fieldRequired')); setIsSubmitting(false); return }
    if (!form.phone) { setLocalError(t('errors.invalidPhone')); setIsSubmitting(false); return }
    // v239.13.0 (C-A-75): require a Turnstile token on open signup.
    if (!turnstileToken) { setLocalError(t('auth.verifyHuman', 'Please complete the verification below.')); setIsSubmitting(false); return }

    const result = await register(form.email, form.password, form.firstName, form.lastName, form.phone, smsOptIn, turnstileToken)
    if (result.success) {
      // PROD-616: the profile basics only, never the form (it carries the password).
      updateProfile({ firstName: form.firstName, lastName: form.lastName, email: form.email, phone: form.phone })
      // v242.12.0: keep the signup draft alive through phone verification so a
      // patient who taps "back" on /verify (wrong phone/email) returns to a
      // pre-filled form to correct + resubmit. Draft is cleared on successful
      // verify (see Verify.jsx), not here.
      navigate('/verify', { replace: true, state: { smsSent: result.smsSent, from: 'signup' } })
    } else {
      setLocalError(result.error || t('errors.somethingWrong'))
      // Turnstile tokens are single-use — re-mount the widget for a fresh challenge.
      setTurnstileToken(null); setTurnstileKey(k => k + 1)
    }
    setIsSubmitting(false)
  }

  const reveal = (delay = 0) => ({
    opacity: contentReady ? 1 : 0,
    transform: contentReady ? 'translateY(0)' : 'translateY(12px)',
    transition: `opacity 0.4s ease ${delay}s, transform 0.4s ease ${delay}s`,
  })

  const headerMessage = isInviteFlow
    ? t('signUp.doctorInviteMessage')
    : (t('auth.createAccountMessage') || "Let's get you set up — it only takes a minute.")

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
    <div className="screen-min-h bg-white flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 pt-8 pb-1">
        <img src={orellaLogo} alt="Orella" className="h-7 object-contain" />
        <button onClick={() => setShowLanguageModal(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 border rounded-full transition-colors"
          style={{ borderColor: '#E2E8F0', color: '#64748B' }}>
          <Globe size={13} className="text-orella-500" /><span className="text-xs font-bold text-orella-700">{currentLanguage.nativeName}</span>
        </button>
      </div>
      
      {/* Get Started Title */}
      <div className="px-5 pt-2 pb-2">
        <h2 className="text-2xl font-bold text-orella-700 flex items-center gap-2">
          {t('auth.getStarted')}
        </h2>
      </div>

      <OrellaPageHeader
        message={headerMessage}
        sessionKey={`signup_${isInviteFlow ? 'invite' : 'standard'}`}
        skipAnimation={!!sessionStorage.getItem('signup_animated')}
        chips={[]}
        onAnimationDone={() => { setContentReady(true); sessionStorage.setItem('signup_animated', 'true') }}
      />
      
      <LanguageSelector isOpen={showLanguageModal} onClose={() => setShowLanguageModal(false)} />
      
      {/* Invite badge */}
      {isInviteFlow && (
        <div style={reveal(0)} className="px-6 pt-2">
          <div className="mb-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-emerald-700 text-sm">{t('signUp.yourAccountIsReadyJust')}</p>
          </div>
        </div>
      )}
      
      {/* Error */}
      <div style={reveal(0.1)} className="px-6">
        {(localError || error) && (
          <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">{localError || error}</div>
        )}
      </div>
      
      {/* Registration form. PROD-1194: it fills the column so Sign Up is pinned at the bottom. */}
      <div style={reveal(0.1)} className="flex-1 flex flex-col">
        <form onSubmit={handleSubmit} className="px-6 flex-1 flex flex-col">
          <p className="text-[11px] text-slate-400 mb-3 text-right"><span className="text-red-500">*</span> required</p>
          <div className="flex gap-2.5 mb-3">
            <div className="flex-1">
              <Label>{t('auth.firstName')} {!isInviteFlow && <Req />}</Label>
              <input type="text" value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})}
                className="w-full focus:outline-none transition-colors"
                style={{ ...fieldStyle, ...(isInviteFlow && form.firstName ? { backgroundColor: '#F8FAFC' } : {}) }}
                onFocus={fieldFocus} onBlur={fieldBlur} required readOnly={isInviteFlow && !!form.firstName} />
            </div>
            <div className="flex-1">
              <Label>{t('auth.lastName')} {!isInviteFlow && <Req />}</Label>
              <input type="text" value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})}
                className="w-full focus:outline-none transition-colors"
                style={{ ...fieldStyle, ...(isInviteFlow && form.lastName ? { backgroundColor: '#F8FAFC' } : {}) }}
                onFocus={fieldFocus} onBlur={fieldBlur} required readOnly={isInviteFlow && !!form.lastName} />
            </div>
          </div>
          
          <div className="mb-1">
            <Label>{t('auth.phone')} {!isInviteFlow && <Req />}</Label>
            <input type="tel" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})}
              className="w-full focus:outline-none transition-colors"
              style={{ ...fieldStyle, ...(isInviteFlow && form.phone ? { backgroundColor: '#F8FAFC' } : {}) }}
              onFocus={fieldFocus} onBlur={fieldBlur} placeholder={t('auth.phonePlaceholder')}
              required={!isInviteFlow} readOnly={isInviteFlow && !!form.phone} />
          </div>
          {isInviteFlow && <div className="mb-3" />}
          {!isInviteFlow && <div className="mb-3" />}
          
          <div className="mb-3">
            <Label>{t('auth.email')} {!isInviteFlow && <Req />}</Label>
            <input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full focus:outline-none transition-colors"
              style={{ ...fieldStyle, ...((isInviteFlow && form.email) || frontEmail ? { backgroundColor: '#F8FAFC' } : {}) }}
              onFocus={fieldFocus} onBlur={fieldBlur} placeholder={t('auth.emailPlaceholder')}
              required={!isInviteFlow} readOnly={(isInviteFlow && !!form.email) || !!frontEmail} />
            {frontEmail && !isInviteFlow && (
              <button type="button" onClick={() => navigate('/email', { state: { email: form.email } })} className="text-xs font-semibold mt-1.5" style={{ color: '#46A8F9' }}>
                {t('emailStart.change', 'Use a different email')}
              </button>
            )}
          </div>
          
          <div className="mb-3">
            <Label>{t('auth.password')}<Req /></Label>
            <div className="relative">
              <input type={showPassword ? 'text' : 'password'} value={form.password}
                onChange={(e) => setForm({...form, password: e.target.value})}
                className="w-full focus:outline-none transition-colors"
                style={{ ...fieldStyle, paddingRight: 44 }} onFocus={fieldFocus} onBlur={fieldBlur}
                placeholder={t('auth.atLeast8Characters')} required minLength={8} autoFocus={isInviteFlow} />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} tabIndex={-1}>
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {form.password.length > 0 && (
            <div className="mb-3 -mt-1 flex flex-wrap gap-x-3.5 gap-y-1">
              {[
                [pwReqs.len, t('auth.pwReqLength', '8+ characters')],
                [pwReqs.letter, t('auth.pwReqLetter', 'A letter')],
                [pwReqs.number, t('auth.pwReqNumber', 'A number')],
              ].map(([met, label], i) => (
                <span key={i} className="inline-flex items-center gap-1.5 text-[12px]" style={{ color: met ? '#2A8A38' : '#94A3B8' }}>
                  <span className="inline-flex items-center justify-center" style={{ width: 14, height: 14, borderRadius: 999, background: met ? '#66D170' : '#E2E8F0' }}>
                    {met && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>}
                  </span>
                  {label}
                </span>
              ))}
            </div>
          )}

          <div className="mb-3">
            <Label>{t('auth.confirmPassword', 'Confirm password')}<Req /></Label>
            <div className="relative">
              <input type={showConfirm ? 'text' : 'password'} value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full focus:outline-none transition-colors"
                style={{ ...fieldStyle, paddingRight: 44 }} onFocus={fieldFocus} onBlur={fieldBlur}
                placeholder={t('auth.confirmPasswordPlaceholder', 'Re-enter your password')} required minLength={8} />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3.5 top-1/2 -translate-y-1/2" style={{ color: '#94A3B8' }} tabIndex={-1}>
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {confirmPassword.length > 0 && !pwMatch && (
              <p className="text-[12px] mt-1.5" style={{ color: '#DC2626' }}>{t('auth.passwordsNoMatch', "Passwords don't match.")}</p>
            )}
            {confirmPassword.length > 0 && pwMatch && (
              <p className="text-[12px] mt-1.5 inline-flex items-center gap-1.5" style={{ color: '#2A8A38' }}>
                <span className="inline-flex items-center justify-center" style={{ width: 14, height: 14, borderRadius: 999, background: '#66D170' }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="4" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>
                </span>
                {t('auth.passwordsMatch', 'Passwords match')}
              </p>
            )}
          </div>

          {!isInviteFlow && (
            <>
              <div className="flex items-start gap-2.5 cursor-pointer py-1 mb-1.5" onClick={() => setSmsOptIn(!smsOptIn)}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                  style={{ borderRadius: 6, backgroundColor: smsOptIn ? '#46A8F9' : 'white', border: smsOptIn ? 'none' : '2px solid #CBD5E1' }}>
                  {smsOptIn && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>}
                </div>
                {/* v242.87.0 (TF b35 #23) — "I didn't check the box but still
                    got an sms to verify."
                    Verified against the code: this is NOT a logic bug. This box
                    is the TCPA consent for ongoing notification SMS. The message
                    he received was the one-time phone-verification code, which
                    is a security step required to create the account at all —
                    unconditional at SignUpScreen -> /verify, and legally distinct
                    from marketing consent. Nothing is gated wrongly.
                    What was wrong is that the label never said so, so the only
                    reading available to a patient who declines and then gets a
                    text is "they ignored my choice". The sentence below closes
                    that gap. Copy fix, deliberately — do not "fix" the OTP. */}
                <SmsConsentText />
              </div>
              <div className="flex items-start gap-2.5 cursor-pointer py-1 mb-2" onClick={() => setAgreeTerms(!agreeTerms)}>
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
                  style={{ borderRadius: 6, backgroundColor: agreeTerms ? '#46A8F9' : 'white', border: agreeTerms ? 'none' : '2px solid #CBD5E1' }}>
                  {agreeTerms && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><path d="M5 12l5 5L19 7"/></svg>}
                </div>
                <p className="text-[12px] leading-relaxed" style={{ color: '#64748B' }}>
                  {t('signUp.byCheckingIAccept')}{' '}
                  <button type="button" className="font-semibold underline bg-transparent border-none p-0 cursor-pointer" style={{ color: '#46A8F9' }}
                    onClick={(e) => { e.stopPropagation(); setShowPolicy('terms') }}>{t('signUp.termsConditions')}</button>
                  {' '}{t('signUp.termsPrivacyConnector')}{' '}
                  <button type="button" className="font-semibold underline bg-transparent border-none p-0 cursor-pointer" style={{ color: '#46A8F9' }}
                    onClick={(e) => { e.stopPropagation(); setShowPolicy('privacy') }}>{t('signUp.privacyPolicy')}</button>.
                </p>
              </div>

              {/* v239.13.0 (C-A-75): bot control — only on open (non-invite) signup. */}
              <div className="flex justify-center mb-1" style={{ minHeight: 65 }}>
                <Turnstile
                  key={turnstileKey}
                  onVerify={setTurnstileToken}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => setTurnstileToken(null)}
                />
              </div>
            </>
          )}
          
          {/* PROD-1194: Sign Up is pinned at the bottom of the screen while the form scrolls. */}
          <BottomActionBar className="-mx-6 [--bar-px:24px]">
            <button type="submit" disabled={loading || isSubmitting || !canSubmit}
              className="w-full font-bold text-base text-white rounded-full active:scale-[0.98] transition-transform disabled:opacity-60"
              style={{ height: 50, background: !canSubmit ? '#CBD5E1' : '#46A8F9', letterSpacing: '0.2px' }}>
              {(loading || isSubmitting)
                ? (isInviteFlow ? 'Activating Account...' : `${t('auth.signUp')}...`)
                : (isInviteFlow ? 'Activate Account' : t('auth.signUp'))}
            </button>
          </BottomActionBar>
        </form>
        
        <p className="text-center text-sm mt-5 bottom-actions [--ba-min:32px]" style={{ color: '#64748B' }}>
          {t('auth.haveAccount')}{' '}
          <button onClick={() => navigate('/email')} className="font-semibold" style={{ color: '#46A8F9' }}>{t('auth.signIn')}</button>
        </p>
      </div>
      
      {showPolicy === 'terms' && <PolicyModal title="Terms & Conditions" type="terms" onClose={() => setShowPolicy(null)} />}
      {showPolicy === 'privacy' && <PolicyModal title="Privacy Policy" type="privacy" onClose={() => setShowPolicy(null)} />}
    </div>
    </ResponsiveWrapper>
  )
}
